import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { CurrencyInput } from '@/components/ui/currency-input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Loader2,
  CreditCard,
  CheckCircle,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'

interface ChargeCustomerDialogProps {
  bookingId: string
  balanceDue: number
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

type ChargeState = 'input' | 'loading' | 'success' | 'error'

export function ChargeCustomerDialog({
  bookingId,
  balanceDue,
  open,
  onOpenChange,
  onSuccess,
}: ChargeCustomerDialogProps) {
  const queryClient = useQueryClient()

  const [amount, setAmount] = useState<number | null>(balanceDue > 0 ? balanceDue : null)
  const [description, setDescription] = useState('')
  const [chargeState, setChargeState] = useState<ChargeState>('input')
  const [errorMessage, setErrorMessage] = useState('')
  const [chargedAmount, setChargedAmount] = useState(0)

  const resetForm = () => {
    setAmount(balanceDue > 0 ? balanceDue : null)
    setDescription('')
    setChargeState('input')
    setErrorMessage('')
    setChargedAmount(0)
  }

  const chargeMutation = useMutation({
    mutationFn: async () => {
      const chargeAmount = amount ?? 0
      if (chargeAmount <= 0) {
        throw new Error('Please enter a valid amount greater than 0')
      }

      setChargeState('loading')

      // Get the current session for auth
      const {
        data: { session },
      } = await supabase.auth.getSession()

      if (!session) {
        throw new Error('You must be logged in to charge customers')
      }

      // Call the edge function
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/charge-booking`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            booking_id: bookingId,
            amount: chargeAmount,
            description: description || undefined,
          }),
        }
      )

      let data
      try {
        data = await response.json()
      } catch {
        // If we can't parse JSON, the edge function might not be deployed
        throw new Error(
          'Unable to connect to payment service. Please ensure edge functions are deployed.'
        )
      }

      if (!response.ok) {
        // Provide helpful error messages for common issues
        if (data.code === 'no_payment_method') {
          throw new Error(
            'No saved payment method found. The customer will need to provide payment information again.'
          )
        }
        if (data.code === 'no_customer') {
          throw new Error(
            'No Stripe customer found. This booking was created before saved payment methods were enabled.'
          )
        }
        throw new Error(data.error || 'Failed to process charge')
      }

      return { amount: chargeAmount, data }
    },
    onSuccess: (result) => {
      setChargedAmount(result.amount)
      setChargeState('success')
      queryClient.invalidateQueries({ queryKey: ['booking-payments', bookingId] })
      queryClient.invalidateQueries({ queryKey: ['booking-detail', bookingId] })
      onSuccess?.()
    },
    onError: (error: Error) => {
      setErrorMessage(error.message)
      setChargeState('error')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    chargeMutation.mutate()
  }

  const handleClose = () => {
    resetForm()
    onOpenChange(false)
  }

  const handleRetry = () => {
    setChargeState('input')
    setErrorMessage('')
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        {chargeState === 'input' && (
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Charge Customer
              </DialogTitle>
              <DialogDescription>
                Charge the customer's saved payment method for additional amounts.
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {/* Balance info */}
              {balanceDue > 0 && (
                <div className="p-3 bg-muted rounded-lg">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Balance Due</span>
                    <span className="font-medium text-red-600">
                      {formatCurrency(balanceDue)}
                    </span>
                  </div>
                </div>
              )}

              {/* Amount */}
              <div className="grid gap-2">
                <Label htmlFor="chargeAmount">Amount to Charge</Label>
                <CurrencyInput
                  value={amount}
                  onChange={setAmount}
                  placeholder="0.00"
                  min={0.5}
                />
                <p className="text-xs text-muted-foreground">
                  Minimum charge: $0.50
                </p>
              </div>

              {/* Description */}
              <div className="grid gap-2">
                <Label htmlFor="chargeDescription">Description (optional)</Label>
                <Textarea
                  id="chargeDescription"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Additional wait time, balance due..."
                  rows={2}
                />
              </div>

              {/* Warning */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Payment will be processed immediately</AlertTitle>
                <AlertDescription>
                  The customer's card on file will be charged{' '}
                  <strong>{formatCurrency(amount || 0)}</strong>. This
                  action cannot be undone from this interface.
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!amount || amount <= 0}
              >
                Charge {formatCurrency(amount || 0)}
              </Button>
            </DialogFooter>
          </form>
        )}

        {chargeState === 'loading' && (
          <div className="py-12 text-center">
            <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-lg font-medium">Processing payment...</p>
            <p className="text-sm text-muted-foreground">
              Please wait while we charge the customer's card.
            </p>
          </div>
        )}

        {chargeState === 'success' && (
          <div className="py-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium">Payment Successful</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {formatCurrency(chargedAmount)} has been charged to the customer's
              card.
            </p>
            <Button className="mt-6" onClick={handleClose}>
              Close
            </Button>
          </div>
        )}

        {chargeState === 'error' && (
          <div className="py-8">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-center">Payment Failed</h3>
            <p className="text-sm text-muted-foreground mt-1 text-center">
              {errorMessage}
            </p>
            <div className="flex gap-2 mt-6 justify-center">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
